import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const createApi = (baseURL = API_URL) => {
  const api = axios.create({
    baseURL,
    headers: { "Content-Type": "application/json" },
  });

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("access_token");
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    },
    (error) => Promise.reject(error),
  );

  api.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
      return Promise.reject(error);
    },
  );

  return api;
};

const createGuestApi = () => {
  const api = axios.create({
    baseURL: API_URL,
    headers: { "Content-Type": "application/json" },
  });
  return api;
};

class BoatApi {
  constructor() {
    this.api = createApi();
  }

  async getAllDecks() {
    const r = await this.api.get("/boat/decks");
    return r.data;
  }
  async getDeck(id) {
    const r = await this.api.get(`/boat/decks/${id}`);
    return r.data;
  }
  async createDeck(data) {
    const r = await this.api.post("/boat/decks", data);
    return r.data;
  }
  async updateDeck(id, data) {
    const r = await this.api.patch(`/boat/decks/${id}`, data);
    return r.data;
  }
  async deleteDeck(id) {
    const r = await this.api.delete(`/boat/decks/${id}`);
    return r.data;
  }
}

class TripsApi {
  constructor() {
    this.api = createApi();
  }

  async getAll(query = {}) {
    const r = await this.api.get("/trips", { params: query });
    return r.data;
  }
  async getById(id) {
    const r = await this.api.get(`/trips/${id}`);
    return r.data;
  }
  async create(data) {
    const r = await this.api.post("/trips", data);
    return r.data;
  }
  async update(id, data) {
    const r = await this.api.patch(`/trips/${id}`, data);
    return r.data;
  }
  async activate(id) {
    const r = await this.api.patch(`/trips/${id}/activate`);
    return r.data;
  }
  async deactivate(id) {
    const r = await this.api.delete(`/trips/${id}/deactivate`);
    return r.data;
  }
}

class SchedulesApi {
  constructor() {
    this.api = createApi();
  }

  async getByTrip(tripId, query = {}) {
    const r = await this.api.get(`/schedules/trip/${tripId}`, {
      params: query,
    });
    return r.data;
  }

  async getById(id) {
    const r = await this.api.get(`/schedules/${id}`);
    return r.data;
  }

  async cancel(id) {
    const r = await this.api.patch(`/schedules/${id}/cancel`);
    return r.data;
  }

  async restore(id) {
    const r = await this.api.patch(`/schedules/${id}/restore`);
    return r.data;
  }

  async updateDate(id, data) {
    const r = await this.api.patch(`/schedules/${id}`, data);
    return r.data;
  }

  async addSingleDate(tripId, date, notes = "") {
    const r = await this.api.post(`/schedules/${tripId}/dates/single`, {
      date,
      notes,
    });
    return r.data;
  }

  async addDateRange(tripId, startDate, endDate, notes = "") {
    const r = await this.api.post(`/schedules/${tripId}/dates/range`, {
      startDate,
      endDate,
      notes,
    });
    return r.data;
  }

  async deleteDate(dateId) {
    const r = await this.api.delete(`/schedules/${dateId}`);
    return r.data;
  }

  async getDatesInRange(tripId, startDate, endDate) {
    const r = await this.api.get(`/schedules/trip/${tripId}/range`, {
      params: { startDate, endDate },
    });
    return r.data;
  }

  async checkDateExists(tripId, date) {
    const r = await this.api.get(`/schedules/trip/${tripId}/check/${date}`);
    return r.data;
  }
}

class PricingApi {
  constructor() {
    this.api = createApi();
  }

  async getDatePricings(tripDateId) {
    const r = await this.api.get(`/pricing/date/${tripDateId}`);
    return r.data;
  }
  async setDatePricing(tripDateId, data) {
    const r = await this.api.post(`/pricing/date/${tripDateId}`, data);
    return r.data;
  }
  async deleteDatePricing(tripDateId, deckId) {
    const r = await this.api.delete(
      `/pricing/date/${tripDateId}/deck/${deckId}`,
    );
    return r.data;
  }
}

class ReservationsApi {
  constructor() {
    this.api = createApi();
  }

  async getAll(query = {}) {
    const r = await this.api.get("/reservations", { params: query });
    return r.data;
  }
  async getById(id) {
    const r = await this.api.get(`/reservations/${id}`);
    return r.data;
  }
  async getMy(query = {}) {
    const r = await this.api.get("/reservations/my", { params: query });
    return r.data;
  }
  async create(data) {
    const r = await this.api.post("/reservations", data);
    return r.data;
  }
  async createAsGuest(data) {
    const api = createGuestApi();
    const r = await api.post("/reservations", data);
    return r.data;
  }
  async adminCreate(data) {
    const r = await this.api.post("/reservations/admin", data);
    return r.data;
  }
  async cancel(id) {
    const r = await this.api.patch(`/reservations/${id}/cancel`);
    return r.data;
  }
  async refund(id) {
    const r = await this.api.patch(`/reservations/${id}/refund`);
    return r.data;
  }
  async remove(id) {
    const r = await this.api.delete(`/reservations/${id}`);
    return r.data;
  }
  async checkAvailability(tripDateId, deckId) {
    const r = await this.api.get(
      `/reservations/availability/${tripDateId}/deck/${deckId}`,
    );
    return r.data;
  }
}

class PaymentsApi {
  constructor() {
    this.api = createApi();
  }

  async getAll(query = {}) {
    const r = await this.api.get("/payments", { params: query });
    return r.data;
  }

  async initiate(data) {
    const r = await this.api.post("/payments/initiate", data);
    return r.data;
  }

  async simulateSuccess(paymentId) {
    const r = await this.api.post(`/payments/${paymentId}/simulate-success`);
    return r.data;
  }

  async confirmPayment(paymentId) {
    const api = createGuestApi();
    const r = await api.post(`/payments/${paymentId}/confirm`);
    return r.data;
  }

  async getByReservation(reservationId) {
    const r = await this.api.get(`/payments/reservation/${reservationId}`);
    return r.data;
  }
}

class TicketsApi {
  constructor() {
    this.api = createApi();
  }

  async getAll(query = {}) {
    const r = await this.api.get("/tickets", { params: query });
    return r.data;
  }
  async getByReservation(reservationId) {
    const r = await this.api.get(`/tickets/reservation/${reservationId}`);
    return r.data;
  }
  async getByNumber(ticketNumber) {
    const r = await this.api.get(`/tickets/number/${ticketNumber}`);
    return r.data;
  }
  async checkIn(id) {
    const r = await this.api.post(`/tickets/${id}/checkin`);
    return r.data;
  }
  async scan(qrPayload) {
    const r = await this.api.post("/tickets/scan", { qrPayload });
    return r.data;
  }
}

class ReportsApi {
  constructor() {
    this.api = createApi();
  }

  async getSummary() {
    const r = await this.api.get("/reports/summary");
    return r.data;
  }
  async getRevenuePerTrip() {
    const r = await this.api.get("/reports/revenue-per-trip");
    return r.data;
  }
  async getReservationsPerDate(tripId) {
    const r = await this.api.get("/reports/reservations-per-date", {
      params: tripId ? { tripId } : {},
    });
    return r.data;
  }
  async getSeatUtilization(tripId) {
    const r = await this.api.get("/reports/seat-utilization", {
      params: tripId ? { tripId } : {},
    });
    return r.data;
  }
  async getCustomers(query = {}) {
    const r = await this.api.get("/reports/customers", { params: query });
    return r.data;
  }
  async getMonthlyRevenue(year) {
    const r = await this.api.get("/reports/monthly-revenue", {
      params: year ? { year } : {},
    });
    return r.data;
  }
}

class UsersApi {
  constructor() {
    this.api = createApi();
  }

  async getAll(query = {}) {
    const r = await this.api.get("/users", { params: query });
    return r.data;
  }
  async getById(id) {
    const r = await this.api.get(`/users/${id}`);
    return r.data;
  }
  async getProfile() {
    const r = await this.api.get("/users/profile");
    return r.data;
  }
  async updateProfile(data) {
    const r = await this.api.patch("/users/profile", data);
    return r.data;
  }
  async create(data) {
    const r = await this.api.post("/users", data);
    return r.data;
  }
  async update(id, data) {
    const r = await this.api.patch(`/users/${id}`, data);
    return r.data;
  }
  async deactivate(id) {
    const r = await this.api.delete(`/users/deactivate${id}`);
    return r.data;
  }
  async activate(id) {
    const r = await this.api.patch(`/users/activate/${id}`);
    return r.data;
  }
  async permanentDelete(id) {
    const r = await this.api.delete(`/users/${id}/permanent`);
    return r.data;
  }
}

class RolesApi {
  constructor() {
    this.api = createApi();
  }

  async getAll() {
    const r = await this.api.get("/roles");
    return r.data;
  }

  async getById(id) {
    const r = await this.api.get(`/roles/${id}`);
    return r.data;
  }

  async create(data) {
    const r = await this.api.post("/roles", data);
    return r.data;
  }

  async update(id, data) {
    const r = await this.api.patch(`/roles/${id}`, data);
    return r.data;
  }

  async delete(id) {
    const r = await this.api.delete(`/roles/${id}`);
    return r.data;
  }
}

class PermissionsApi {
  constructor() {
    this.api = createApi();
  }

  async getAll() {
    const r = await this.api.get("/permissions");
    return r.data;
  }
}

class PromoCodesApi {
  constructor() {
    this.api = createApi();
  }

  // Get all promo codes (paginated)
  async getAll(params = {}) {
    const r = await this.api.get("/promo-codes", { params });
    return r.data;
  }

  // Get single promo code by ID
  async getById(id) {
    const r = await this.api.get(`/promo-codes/${id}`);
    return r.data;
  }

  // Bulk generate promo codes (admin)
  async bulkCreate(data) {
    const r = await this.api.post("/promo-codes", data);
    return r.data;
  }

  // Validate a promo code (public - no auth required)
  async validate(code) {
    const api = createGuestApi();
    const r = await api.post("/promo-codes/validate", { code });
    return r.data;
  }

  // Deactivate a promo code (admin)
  async deactivate(id) {
    const r = await this.api.post(`/promo-codes/${id}/deactivate`);
    return r.data;
  }

  // Delete a promo code (admin)
  async remove(id) {
    const r = await this.api.delete(`/promo-codes/${id}`);
    return r.data;
  }
}

class RefundsApi {
  constructor() {
    this.api = createApi();
  }

  async getAll(query = {}) {
    const r = await this.api.get("/refunds", { params: query });
    return r.data;
  }

  async getById(id) {
    const r = await this.api.get(`/refunds/${id}`);
    return r.data;
  }

  // Get ticket details by ticket number (for mobile refund flow)
  async getTicketByNumber(ticketNumber) {
    const r = await this.api.get(`/tickets/number/${ticketNumber}`);
    return r.data;
  }

  // Request refund by ticket number
  async requestByTicketNumber(ticketNumber, data = {}) {
    const r = await this.api.post("/refunds/by-ticket", {
      ticketNumber,
      ...data,
    });
    return r.data;
  }

  async approve(id, adminNotes) {
    const r = await this.api.post(`/refunds/${id}/approve`, { adminNotes });
    return r.data;
  }

  async reject(id, adminNotes) {
    const r = await this.api.post(`/refunds/${id}/reject`, { adminNotes });
    return r.data;
  }

  async complete(id, transactionReference) {
    const r = await this.api.post(`/refunds/${id}/complete`, {
      transactionReference,
    });
    return r.data;
  }
}

class AuditApi {
  constructor() {
    this.api = createApi();
  }

  async getAll(query = {}) {
    const r = await this.api.get("/audit-logs", { params: query });
    return r.data;
  }
}

class RequestsApi {
  constructor() {
    this.api = createApi();
  }

  async getAll(query = {}) {
    const r = await this.api.get("/requests", { params: query });
    return r.data;
  }
  async approve(id, adminNote) {
    const r = await this.api.post(`/requests/${id}/approve`, { adminNote });
    return r.data;
  }
  async reject(id, adminNote) {
    const r = await this.api.post(`/requests/${id}/reject`, { adminNote });
    return r.data;
  }
}

class WalletApi {
  constructor() {
    this.api = createApi();
  }

  async getUserWallet(userId, query = {}) {
    const r = await this.api.get(`/wallet/${userId}`, { params: query });
    return r.data;
  }
  async adjust(userId, amount, note) {
    const r = await this.api.post(`/wallet/${userId}/adjust`, { amount, note });
    return r.data;
  }
}

export const boatApi = new BoatApi();
export const auditApi = new AuditApi();
export const requestsApi = new RequestsApi();
export const walletApi = new WalletApi();
export const refundsApi = new RefundsApi();
export const tripsApi = new TripsApi();
export const schedulesApi = new SchedulesApi();
export const pricingApi = new PricingApi();
export const reservationsApi = new ReservationsApi();
export const paymentsApi = new PaymentsApi();
export const ticketsApi = new TicketsApi();
export const reportsApi = new ReportsApi();
export const usersApi = new UsersApi();
export const rolesApi = new RolesApi();
export const permissionsApi = new PermissionsApi();
export const promoCodesApi = new PromoCodesApi();
